<template>
  <div class="list--wrap">
    <table class="ab__list__table">
      <caption hidden>A/B Test List</caption>
      <colgroup>
        <col width="150px">
        <col width="*">
        <col width="80px">
        <col width="121px">
        <col width="120px">
        <col width="120px">
        <col width="116px">
        <col width="60px">
      </colgroup>
      <thead>
        <tr>
          <th>
            <sort-options
            columnName="abTestType"
            :sortColumn="sortColumn"
            :isSortAsc="isSortAsc"
            @onSortOptionChange="onSortOptionChange">
              <span v-html="$t('common.table.header.abType')"></span>
            </sort-options>
          </th>
          <th>
            <sort-options
              columnName="pageName"
              :sortColumn="sortColumn"
              :isSortAsc="isSortAsc"
              @onSortOptionChange="onSortOptionChange">
              <span v-html="$t('common.table.header.test')"></span>
            </sort-options>
          </th>
          <th>
            <span v-html="$t('common.table.header.setting')"></span>
          </th>
          <th>
            <sort-options
              columnName="state"
              :sortColumn="sortColumn"
              :isSortAsc="isSortAsc"
              @onSortOptionChange="onSortOptionChange">
              <span v-html="$t('common.table.header.status')"></span>
            </sort-options>
          </th>
          <th>
            <sort-options
              columnName="startDate"
              :sortColumn="sortColumn"
              :isSortAsc="isSortAsc"
              @onSortOptionChange="onSortOptionChange">
              <span v-html="$t('common.startDate')"></span>
            </sort-options>
          </th>
          <th>
            <sort-options
              columnName="endDate"
              :sortColumn="sortColumn"
              :isSortAsc="isSortAsc"
              @onSortOptionChange="onSortOptionChange">
              <span v-html="$t('common.endDate')"></span>
            </sort-options>
          </th>
          <th>
            <span v-html="$t('common.table.header.report')"></span>
          </th>
          <th>
          </th>
        </tr>
      </thead>
      <tbody>
      <tr v-for="(item, index) in sortedList" :key="index">
        <td class="col__method align-center">
          <p class="page-method">
            <i class="icon" :class="`icon--ab--list--${item.abTestType}`"></i>
            <span>{{ $t('ab.' + item.abTestType) }}</span>
          </p>
        </td>

        <td :disabled="!item.canViewReport"
            :class="{disabled: !item.canViewReport}"
            @click="onMoveABreport(item)"
            class="col__name align-left">
          <p class="page-name">{{ item.pageName }}</p>
          <p class="page-url" v-if="item.abTestType === 'url'">{{ item.url }}</p>
          <p class="page-edited-item" v-else >{{ $t('ab.edit') }} : <span class="count">{{ item.textCount }}</span></p>
        </td>

        <td class="col__setting">
          <button :class="{disabled: isShared}" @click.stop="showSettingOption($event)" class="icon icon--dashboard-setting"></button>
          <div class="setting__option--wrap" style="display: none">
            <ul>
              <li @click="onModifyStatus(item, 'run')" v-if="item.canRunChange && !(isSPA && applyData.bLogin)">
                {{ $t('ab.changeState.run') }}
              </li>
              <li @click="onModifyStatus(item, 'stop')" v-if="item.canStopChange">
                {{ $t('ab.state.stop') }}
              </li>
              <li @click="onMoveEditPage(item)">
                {{ $t('ab.changeSetting') }}
              </li>
            </ul>
          </div>
        </td>

        <td class="col__status" :class="uiCurrentState(item)">
          <span class="status-error"
                v-if="uiCurrentState(item) !== 'tempStop' && (uiCurrentState(item) === 'uninstalled' || uiCurrentState(item) === 'error')" >
            <ui-icon-tooltip arrowCenter>
              <i class="icon icon--alert-triangle-size16"></i>
              <span v-html="$t('common.trackingCode.required')"></span>
            </ui-icon-tooltip>
          </span>
          <span class="status-text" :class="{'status-analyze': uiCurrentState(item) === 'analyzing'}">{{ $t(`page.state.${uiCurrentState(item)}`) }}</span>
        </td>

        <td class="col__regi-date">
          <span>{{ computedDate(item.startDate) }}</span>
        </td>

        <td class="col__regi-date">
          <span>{{ computedDate(item.endDate) }}</span>
        </td>

        <td class="col__confirm">
          <button @click="onMoveABreport(item)"
                  :disabled="!item.canViewReport"
                  :class="{ disabled: !item.canViewReport }">
            <i class="icon icon--arrow-green-size16"></i>
            <span class="btn-text">{{ $t('common.view') }}</span>
          </button>
        </td>

        <td class="col__delete">
          <button v-if="!isShared" @click="onDeleteConfirm(item)" class="icon icon--trash"></button>
        </td>
      </tr>
      </tbody>
    </table>

    <install-checker v-if="installCheckerData"
      :checkerData="installCheckerData"
      @onDone="onInstallCheckDone"/>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { modifyPage, deletePage } from '@/api/abtest'
import { create } from 'vue-modal-dialogs'
import { cloneDeep, utcToLocalTimezone } from '@/libs/utils'
import AppDialog from '@/components/ui/AppDialog'
import InstallChecker from '@/components/ui/InstallChecker'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import SortOptions from '@/components/ui/SortOptions'
import { format } from 'date-fns'

const confirmDelete = create(AppDialog, 'customData')

export default {
  components: {
    InstallChecker,
    UiIconTooltip,
    SortOptions
  },
  data () {
    return {
      sortColumn: 'startDate',
      isSortAsc: false,
      installCheckerData: null
    }
  },
  props: [
    'abPageList'
  ],
  computed: {
    ...mapState([
      'applyData'
    ]),

    sortedList () {
      const list = cloneDeep(this.abPageList)
      return list.sort((a, b) => {
        const modifier = this.isSortAsc ? 1 : -1
        if (a[this.sortColumn] < b[this.sortColumn]) return -1 * modifier
        if (a[this.sortColumn] > b[this.sortColumn]) return 1 * modifier
        return 0
      })
    },
    aid () {
      return this.$route.params.aid
    },
    isShared () {
      return this.applyData.meta.shared
    },
    isSPA () {
      return this.applyData.meta.isSPA
    }
  },
  methods: {
    ...mapActions([
      'storeABList'
    ]),
    computedDate (value) {
      return format(utcToLocalTimezone(value, this.applyData.timezoneName), 'YYYY-MM-DD')
    },
    uiCurrentState (bPage) {
      return bPage.uiState
    },
    showSettingOption () {
      if (this.isShared) return false
      this.hideSettingOption()
      event.target.nextSibling.style.display = 'inline-block'
    },
    hideSettingOption () {
      const settingOptionEl = document.querySelectorAll('.col__setting .setting__option--wrap')
      for (const item of settingOptionEl) {
        item.style.display = 'none'
      }
    },
    onCheckInstall (item) {
      this.installCheckerData = {
        type: 'ab',
        aid: item.aid,
        url: item.url
      }
    },
    onModifyStatus (item, state) {
      const values = {
        orgAid: this.applyData._id,
        abAid: item.aid,
        pageName: item.pageName,
        state: state,
        abTestType: item.abTestType
      }

      modifyPage(values).then(res => {
        return this.storeABList(this.aid)
      }).catch(console.error)
    },
    onDeleteConfirm (item) {
      if (this.isShared) return false
      confirmDelete({
        type: 'confirm',
        iconClass: 'icon--dialog-trash',
        title: this.$t('dialog.abDelete.title'),
        description: this.$t('dialog.abDelete.desc', { pageName: item.pageName })
      }).then(result => {
        if (result) {
          const values = { orgAid: this.applyData._id, abAid: item.aid, abTestType: item.abTestType }
          deletePage(values)
            .then(() => {
              return this.storeABList(this.aid)
            })
            .catch(console.error)
        }
      })
    },
    onMoveEditPage (item) {
      this.hideSettingOption()
      let pageName = item.abTestType === 'url' ? 'ab.edit' : 'ab.edit.text'
      this.$router.push({
        name: pageName,
        params: { aid: this.aid, abAid: item.aid, abTestType: item.abTestType }
      })
    },
    onMoveABreport (item) {
      if (!item.canViewReport) {
        return false
      }
      this.$router.push({
        name: 'ab.view',
        params: { aid: this.aid, abAid: item.aid, abTestType: item.abTestType }
      })
    },
    onSortOptionChange (column, isAsc) {
      if (this.sortColumn === column && this.isSortAsc === isAsc) {
        return
      }
      this.sortColumn = column
      this.isSortAsc = isAsc
    },
    onInstallCheckDone (needReload) {
      this.installCheckerData = null

      if (needReload) {
        this.storeABList(this.aid).catch(console.error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.list--wrap {
  .ab__list__table {
    table-layout: fixed;
    margin-top: 20px;
    width: 100%;
    font-size: 13px;
    thead tr {
      height: 50px;
      background-color: #555555;
      font-weight: bold;
      line-height: 1.23;
      color: #ffffff;
      text-align: center;

      th.clickable { cursor: pointer; }

      th:nth-of-type(1){
        position: relative;
        min-width: 120px;
        &:before {
          content: '';
          display: inline-block;
          width: 15px;
          height: 50px;
          position: absolute;
          top: 0;
          left: -5px;
          background-color: #555;
          border-radius: 5px;
        }
        &.align-left {
          text-align: left;
          padding-left: 10px;
        }
      }

      th:nth-last-of-type(1) {
        position: relative;
        &:after {
          content: '';
          display: inline-block;
          width: 10px;
          height: 50px;
          position: absolute;
          top: 0;
          right: -5px;
          background-color: #555;
          border-radius: 5px;
        }
      }

      span {
        position: relative;
        display: inline-block;
        .icon {
          position: absolute;
          top: 50%;
          right: -19px;
          transform: translateY(-50%);
        }
      }
    }
    tbody tr {
      height: 80px;
      text-align: center;
      border-top: 1px solid #ebebeb;
      box-sizing: border-box;

      &:nth-of-type(1) {
        border-top: none;
      }

      &:nth-last-of-type(1) {
        border-bottom: 1px solid #ebebeb;
      }

      &:hover {
        @include hoverStyle;
      }

      td {
        font-size: 13px;
        color: #666;

        &.align-left {
          padding-left: 10px;
          text-align: left;
        }

        &.col__name {
          line-height: 1.4;

          &:hover:not(.disabled) {
            cursor: pointer;
            .page-name {
              color: #1199af !important;
            }
          }
          .page-name {
            font-size: 14px;
            font-weight: bold;
            color: #2f2f2f;
            @include textEllipsis;
          }
          .page-url {
            margin-top: 15px;
            @include textEllipsis;
          }
          .page-edited-item {
            margin-top: 15px;
            @include textEllipsis;

            span.count {
              color: #1199af
            }
          }
        }

        &.col__method {
          p {
            padding-left: 10px;
            position: relative;
            i {
              position: absolute;
              top: 50%;
              transform: translate(0, -50%);
              left: 10px;
            }
            span {
              position: absolute;
              top: 50%;
              transform: translate(0, -50%);
              left: 55.5px;
            }
          }
        }

        &.col__setting {
          position: relative;
          button {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-position: center;

            &:hover {
              @include hoverStyle;
            }

            &.disabled {
              pointer-events: none;
              opacity: 0.2;
            }
          }
          .setting__option--wrap {
            position: absolute;
            top: 60px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1;
            ul {
              width: 75px;
              border-radius: 6px;
              box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
              background-color: #ffffff;
              overflow: hidden;

              li {
                cursor: pointer;
                height: 37px;
                line-height: 35px;
                font-size: 14px;
                color: #444444;

                &:hover {
                  background-color: #ebebeb;
                }
              }
            }
          }
        }

        &.col__status {
          &.run {
            color: $stateRunColor;
          }
          &.analyzing {
            color: $stateAnalyzingColor;
          }
          &.stop,
          &.tempStop {
            color: $stateStopColor;
          }
          &.uninstalled,
          &.error,
          &.endAsError {
            color: $stateUnInstalledColor;

            & .status-text {
              margin-left: 5px;
              vertical-align: middle;
            }
          }
          &.scheduledRun {
            color: $stateReservationColor;
          }
          &.endByPvLimit {
            color: $stateEndColor;
          }
          &.endByAnalysisDate {
            color: $stateEndColor;
          }

          .status-error {
            position: relative;

            /deep/ .tooltip {
              .icon {
                width: 16px;
                height: 16px;
                background-image: url(../../assets/img/icon_alert_triangle_size16.svg);
              }
              .text {
                .icon {
                  margin-right: 5px;
                  position: relative;
                  top: 2px;
                }
              }
            }
          }

          .status-analyze {
            color: #43b6c9;
          }
        }

        &.col__confirm {
          button {
            @include buttonSizeSmall_includeIcon;
            border: 1px solid #57ab00;
            background-color: #fff;

            &:hover {
              &:after {
                @include hoverStyle;
              }
            }

            &.disabled {
              cursor: default;
              opacity: 0.3;
            }

            .btn-text{
              color: #57ab00;
            }
          }
        }

        &.col__tracking-code {
          .btn__check-code {
            @include buttonTypeSmall;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            font-size: 13px;
            font-weight: bold;
            color: #444444;

            &:hover {
              @include hoverStyle;
            }
          }
        }

        &.col__delete {
          button {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-position: center;

            &:hover {
              @include hoverStyle;
            }

            &.disabled {
              pointer-events: none;
              opacity: 0.2;
            }
          }
        }
      }
    }
  }
}
</style>
